import React, { useCallback, useEffect, useState } from 'react';
import { Button, Typography, Spin, Modal } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../../../context/ThemeContext';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../redux/store';
import {
  createOption,
  updateOption,
  deleteOption,
  bulkUpdateOptionOrder,
} from '../../store/optionsSlice';
import {
  CreateOptionButton,
  EditOptionsContainer,
  CursorPointerText,
  StickyButtonContainer,
  BreadcrumbSpace,
  TitleWithMargin,
  EditOptionsLoadingContainer,
  SaveButtonAutoWidth,
  BreadcrumbSpaceWrap,
  SecondaryTextSmall,
  OptionPreviewContainer,
  OptionPreviewPrice,
} from '../Styles';
import { OptionsList } from './OptionsList';
import { OptionForm } from './OptionForm';
import { EditOptionsProps, NewOptionData, Option } from './types';
import { notification } from '../../../../commonComponents/Notification/Notification';

const EditOptions: React.FC<EditOptionsProps> = ({
  customizationData,
  handleBackClick,
  handleSubmit,
  handleInputChange,
  selectedItem,
  handleRefresh,
  handleSilentRefresh,
  width,
}) => {
  const { isDarkMode } = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const { creating, updating } = useSelector(
    (state: RootState) => state.options
  );
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const [isCreatingNewOption, setIsCreatingNewOption] = useState(false);
  const [isEditingOption, setIsEditingOption] = useState(false);
  const [editingOptionId, setEditingOptionId] = useState<number | string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmittingItem, setIsSubmittingItem] = useState(false);
  const [soldOutLoading, setSoldOutLoading] = useState<number | string | null>(null);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [optionToDelete, setOptionToDelete] = useState<Option | null>(null);
  const [isDeletingOption, setIsDeletingOption] = useState(false);
  const [newOptionData, setNewOptionData] = useState<NewOptionData>({
    option: '',
    price: '',
    description: '',
    sold_out: false,
  });

  const optionsBorderColor = isDarkMode ? '#434343' : '#e8e8e8';

  useEffect(() => {
    const customizationOptions = customizationData?.options || customizationData?.option;
    
    if (!customizationOptions || !Array.isArray(customizationOptions) || customizationOptions.length === 0) {
      setSelectedOptions([]);
      return;
    }
    const selectedOptionObjects: Option[] = customizationOptions
      .filter((opt: Option) => opt?.id).sort((a, b) => (a.sequence_number || 0) - (b.sequence_number || 0));
    setSelectedOptions(selectedOptionObjects);
  }, [customizationData?.options, customizationData?.option]);

  const handleCreateOption = useCallback(
    async (newOptionData: NewOptionData) => {
      if (!selectedItem) {
        notification.error('Missing Required Fields', 'Please select a customization first.', 7);
        return null;
      }

      try {
        await dispatch(
          createOption({
            option: newOptionData.option,
            price: parseFloat(newOptionData.price),
            description: newOptionData.description,
            customization: selectedItem,
          })
        ).unwrap();

        return true;
      } catch (err: any) {
        notification.error('Error', err || 'Failed to create the option.', 5);
        return null;
      }
    },
    [selectedItem, dispatch]
  );

  const handleUpdateOption = useCallback(
    async (optionId: number | string, updateData: Partial<NewOptionData>) => {
      try {
        const payload: any = {};
        if (updateData.option !== undefined) payload.option = updateData.option;
        if (updateData.price !== undefined) payload.price = parseFloat(updateData.price);
        if (updateData.description !== undefined) payload.description = updateData.description;
        if (updateData.sold_out !== undefined) payload.sold_out = updateData.sold_out;

        const result = await dispatch(
          updateOption({ id: optionId, data: payload })
        ).unwrap();

        return result;
      } catch (err: any) {
        notification.error('Error updating option', err || 'Failed to update the option.', 5);
        return null;
      }
    },
    [dispatch]
  );

  const saveOrder = useCallback(async (optionsToSave: Option[]) => {
    console.log('optionsToSave', optionsToSave);
    if (optionsToSave.length === 0) return;
    
    try {
      // Update sequence_number for each option individually via /options/optionID API
      const updatePromises = optionsToSave.map((option, index) => {
        const newSequenceNumber = index;
        // Only update if sequence_number actually changed
        if (option.sequence_number !== newSequenceNumber) {
          return dispatch(
            updateOption({
              id: option.id,
              data: { sequence_number: newSequenceNumber },
            })
          ).unwrap();
        }
        return Promise.resolve();
      });

      await Promise.all(updatePromises);
      await handleRefresh();
    } catch (error) {
      console.error('Failed to save order:', error);
    }
  }, [dispatch, handleRefresh]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveOrder(selectedOptions);
    } finally {
      setIsSaving(false);
    }
  };

  const handleOrderChange = useCallback(async (
    draggedOption: Option,
    fromIndex: number,
    toIndex: number,
  ) => {
    if (!draggedOption?.id) return;

    try {
      const sortedOptions = [...selectedOptions].sort(
        (a, b) => (a.sequence_number || 0) - (b.sequence_number || 0)
      );

      const rangeStart = Math.min(fromIndex, toIndex);
      const rangeEnd = Math.max(fromIndex, toIndex);

      const sequencesInRange = sortedOptions
        .slice(rangeStart, rangeEnd + 1)
        .map((opt) => opt.sequence_number ?? 0);

      const newOrder = sortedOptions.map((o) => o.id);
      newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, draggedOption.id);

      const updates: { id: number; sequence_number: number }[] = [];
      // Only update options that are within the affected range to minimize API calls
      for (let i = rangeStart; i <= rangeEnd; i++) {
        const opt = sortedOptions.find((o) => o.id === newOrder[i]);
        if (opt) updates.push({ id: opt.id as number, sequence_number: sequencesInRange[i - rangeStart] });
      }

      if (updates.length === 0) return;

      await dispatch(bulkUpdateOptionOrder(updates)).unwrap();
      notification.success('Order updated', 'Options reordered successfully.', 3);
      await (handleSilentRefresh ?? handleRefresh)();
    } catch (error) {
      console.error('Failed to save order:', error);
    }
  }, [dispatch, handleRefresh, handleSilentRefresh, selectedOptions]);


  const handleOptionRemove = useCallback(
    (optionId: number | string) => {
      if (!optionId) {
        return;
      }

      const optionToRemove = selectedOptions.find(
        (opt) => opt && opt.id && (opt.id === optionId || opt.id.toString() === optionId.toString())
      );

      if (!optionToRemove) {
        return;
      }

      setOptionToDelete(optionToRemove);
      setIsDeleteAlertOpen(true);
    },
    [selectedOptions]
  );

  const confirmDeleteOption = useCallback(async () => {
    if (!optionToDelete || !optionToDelete.id) {
      return;
    }

    setIsDeletingOption(true);
    try {
      await dispatch(deleteOption(optionToDelete.id)).unwrap();
      await handleRefresh();
      setIsDeleteAlertOpen(false);
      setOptionToDelete(null);
      notification.success('Option removed', 'The option has been removed from this customization.', 3);
    } catch (error: any) {
      notification.error('Error', error || 'Failed to delete the option.', 5);
    } finally {
      setIsDeletingOption(false);
    }
  }, [optionToDelete, dispatch, handleRefresh]);

  const cancelDeleteOption = useCallback(() => {
    setIsDeleteAlertOpen(false);
    setOptionToDelete(null);
    setIsDeletingOption(false);
  }, []);

  const handleNewOptionDataChange = useCallback((field: string, value: any) => {
    setNewOptionData((prevData) => ({ ...prevData, [field]: value }));
  }, []);

  const handleSubmitItem = async () => {
    const missingFields: string[] = [];
    if (!newOptionData.option || newOptionData.option.trim() === '') {
      missingFields.push('Option Name');
    }
    if (!newOptionData.price || isNaN(Number(newOptionData.price))) {
      missingFields.push('Price');
    }
    if (!selectedItem) {
      missingFields.push('Customization ID');
    }

    if (missingFields.length > 0) {
      const formattedMissingFields = missingFields.map((field) => `• ${field}`).join('\n');
      notification.error('Missing Required Fields', `Please fill out the following required fields:\n${formattedMissingFields}`, 7);
      return;
    }

    setIsSubmittingItem(true);
    try {
      if (isEditingOption && editingOptionId) {
        await handleUpdateOption(editingOptionId, newOptionData);
        notification.success('Option updated', 'The option was successfully updated.', 5);
        await handleRefresh();
      } else {
        await handleCreateOption(newOptionData);
        await handleRefresh();
        notification.success('Option Created', 'The new option has been created successfully.', 5);
      }
      resetCreateNewOptionState();
      setIsCreatingNewOption(false);
    } catch (error: any) {
      notification.error('An error occurred', 'Unable to save the option.', 5);
    } finally {
      setIsSubmittingItem(false);
    }
  };

  const resetCreateNewOptionState = useCallback(() => {
    setNewOptionData({
      option: '',
      price: '',
      description: '',
      sold_out: false,
    });
    setIsEditingOption(false);
    setEditingOptionId(null);
  }, []);

  const handleBackFromCreateNewOption = useCallback(() => {
    resetCreateNewOptionState();
    setIsCreatingNewOption(false);
  }, [resetCreateNewOptionState]);

  const submitSoldOut = useCallback(async (option: Option, updatedOption: Partial<Option>) => {
    setSoldOutLoading(option.id);
    try {
      await handleUpdateOption(option.id, {
        option: updatedOption.option || option.option,
        price: updatedOption.price !== undefined ? updatedOption.price.toString() : option.price?.toString() || '',
        description: updatedOption.description || option.description || '',
        sold_out: updatedOption.sold_out,
      });
      await handleRefresh();
      notification.success('Option updated', 'The option was successfully updated.', 5);
    } catch (error) {
      notification.error('Error updating option', 'Failed to update the option.', 5);
    } finally {
      setSoldOutLoading(null);
    }
  }, [handleUpdateOption, handleRefresh]);

  const handleEditOption = useCallback(
    (option: Option, isSoldOut?: boolean) => {
      if (isSoldOut) {
        const updatedOption = {
          option: option.option,
          price: option.price !== undefined ? option.price : 0,
          description: option.description || '',
          sold_out: !option.sold_out,
        };
        submitSoldOut(option, updatedOption);
      } else {
        setIsCreatingNewOption(false);
        setIsEditingOption(true);
        setEditingOptionId(option.id);
        setNewOptionData({
          option: option.option,
          price: option.price !== undefined ? option.price.toString() : '',
          description: option.description || '',
          sold_out: option.sold_out || false,
        });
      }
    },
    [submitSoldOut]
  );

  const isFixed = !width;

  return (
    <EditOptionsContainer $width={width} $isFixed={isFixed}>
      {!isCreatingNewOption && !isEditingOption && (
        <>
          <BreadcrumbSpace>
            <Button
              type="text"
              icon={<FontAwesomeIcon icon={faChevronLeft} />}
              onClick={handleBackClick}
              size="small"
            />
            <CursorPointerText onClick={handleBackClick}>
              Customizations
            </CursorPointerText>
            <Typography.Text type="secondary"> / Options</Typography.Text>
          </BreadcrumbSpace>

          <TitleWithMargin level={4}>
            Edit Options
          </TitleWithMargin>

          <CreateOptionButton
            onClick={() => {
              setIsCreatingNewOption(true);
              resetCreateNewOptionState();
            }}
          >
            + Create a new option
          </CreateOptionButton>

          {selectedOptions.length === 0 ? (
            <EditOptionsLoadingContainer>
              <Typography.Text type="secondary">No options available</Typography.Text>
            </EditOptionsLoadingContainer>
          ) : (
            <OptionsList
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
              handleEditOption={handleEditOption}
              handleOptionRemove={handleOptionRemove}
              optionsBorderColor={optionsBorderColor}
              selectedItem={selectedItem}
              soldOutLoading={soldOutLoading}
              onOrderChange={handleOrderChange}
            />
          )}
          
          <SaveButtonAutoWidth 
            onClick={handleSave}
            loading={isSaving}
            icon={isSaving ? <Spin size="small" /> : null}
          >
            Save
          </SaveButtonAutoWidth>
        </>
      )}

      {(isCreatingNewOption || isEditingOption) && (
        <>
          <BreadcrumbSpaceWrap wrap>
            <Button
              type="text"
              icon={<FontAwesomeIcon icon={faChevronLeft} />}
              onClick={
                isEditingOption
                  ? () => {
                      setIsEditingOption(false);
                      setEditingOptionId(null);
                      setNewOptionData({
                        option: '',
                        price: '',
                        description: '',
                        sold_out: false,
                      });
                    }
                  : handleBackFromCreateNewOption
              }
              size="small"
            />
            <CursorPointerText onClick={handleBackClick}>
              Customizations
            </CursorPointerText>
            <CursorPointerText
              onClick={
                isEditingOption
                  ? () => {
                      setIsEditingOption(false);
                      setEditingOptionId(null);
                      setNewOptionData({
                        option: '',
                        price: '',
                        description: '',
                        sold_out: false,
                      });
                    }
                  : handleBackFromCreateNewOption
              }
            >
              / Options
            </CursorPointerText>
            <SecondaryTextSmall type="secondary">
              {isEditingOption ? ' / Edit Item' : ' / Create new option'}
            </SecondaryTextSmall>
          </BreadcrumbSpaceWrap>

          <TitleWithMargin level={4}>
            {isEditingOption ? 'Edit Item' : 'Create new option'}
          </TitleWithMargin>
          
          <OptionForm
            newOptionData={newOptionData}
            handleNewOptionDataChange={handleNewOptionDataChange}
          />

          <StickyButtonContainer>
            <SaveButtonAutoWidth 
              onClick={handleSubmitItem}
              loading={isSubmittingItem || creating || updating}
              icon={(isSubmittingItem || creating || updating) ? <Spin size="small" /> : null}
            >
              {isEditingOption ? 'Save Changes' : 'Create option'}
            </SaveButtonAutoWidth>
          </StickyButtonContainer>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Modal
        open={isDeleteAlertOpen}
        onCancel={cancelDeleteOption}
        onOk={confirmDeleteOption}
        title="Delete Option"
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ 
          danger: true,
          loading: isDeletingOption,
          icon: isDeletingOption ? <Spin size="small" /> : null
        }}
      >
        <Typography.Text>Are you sure you want to delete this option?</Typography.Text>
        {optionToDelete && (
          <OptionPreviewContainer>
            <Typography.Text strong>{optionToDelete.option}</Typography.Text>
            <div>
              <OptionPreviewPrice type="secondary">
                Price: ${(optionToDelete.price || 0).toFixed(2)}
              </OptionPreviewPrice>
            </div>
          </OptionPreviewContainer>
        )}
      </Modal>
    </EditOptionsContainer>
  );
};

export default EditOptions;

